/* eslint-disable no-magic-numbers */
import Client from "@walletconnect/sign-client";
import {PairingTypes, SessionTypes} from "@walletconnect/types";
import {Web3Modal} from "@web3modal/standalone";
import {getAppMetadata, getSdkError} from "@walletconnect/utils";
import {formatJsonRpcRequest} from "@json-rpc-tools/utils";
import {SignerTransaction, SupportedNetwork} from "@tinymanorg/tinyman-js-sdk";

import {
  ALGORAND_CHAINS,
  DEFAULT_APP_METADATA,
  DEFAULT_CHAINS,
  DEFAULT_PROJECT_ID,
  DEFAULT_RELAY_URL,
  DEFAULT_REQUIRED_NAMESPACES,
  FIREBLOCKS_WALLET_ID
} from "./WalletConnect.constants";
import {base64ToUint8Array} from "../../util/blob/blobUtils";
import {getSignTxnRequestParams} from "./WalletConnect.utils";
import {PREFERRED_NETWORK} from "../../constants";
import {getPreferredTheme} from "../../util/theme/themeUtils";
import {SwapWidgetAppTheme} from "../../widgetTypes";

class WalletConnectManager {
  private client: Client | undefined = undefined;
  private session: SessionTypes.Struct | undefined;
  public pairings: PairingTypes.Struct[] = [];
  private web3Modal: Web3Modal = new Web3Modal({
    projectId: DEFAULT_PROJECT_ID,
    themeMode: getPreferredTheme() === SwapWidgetAppTheme.Dark ? "dark" : "light",
    walletConnectVersion: 2,
    explorerRecommendedWalletIds: [FIREBLOCKS_WALLET_ID]
  });

  chainId: string;

  constructor(network: SupportedNetwork) {
    this.chainId = ALGORAND_CHAINS[network];
  }

  async initialize({
    onDisconnectSuccessful,
    onSessionUpdate
  }: {
    onDisconnectSuccessful: () => Promise<void>;
    onSessionUpdate: (accountAddresses: string[]) => Promise<void>;
  }) {
    try {
      this.client = await Client.init({
        relayUrl: DEFAULT_RELAY_URL,
        projectId: DEFAULT_PROJECT_ID,
        metadata: getAppMetadata() || DEFAULT_APP_METADATA
      });

      await this.subscribeToEvents({onDisconnectSuccessful, onSessionUpdate});
      await this.checkPersistedState();
    } catch (error) {
      console.error("Failed to initialize WalletConnect:", error);
      throw error;
    }
  }

  private async checkPersistedState() {
    if (!this.client) {
      throw new Error("WalletConnect is not initialized");
    }
    if (this.client.session.length) {
      const lastKeyIndex = this.client.session.keys.length - 1;
      const restoredSession = this.client.session.get(
        this.client.session.keys[lastKeyIndex]
      );

      await this.onSessionConnected(restoredSession);
    }
  }

  private onSessionConnected(session: SessionTypes.Struct) {
    const updatedSession: SessionTypes.Struct = {...session};

    this.session = updatedSession;
  }

  subscribeToEvents({
    onDisconnectSuccessful,
    onSessionUpdate
  }: {
    onDisconnectSuccessful: () => Promise<void>;
    onSessionUpdate: (accountAddresses: string[]) => Promise<void>;
  }) {
    if (!this.client) {
      throw new Error("WalletConnect is not initialized");
    }

    this.client.on("session_update", async ({topic, params}) => {
      const updatedSession: SessionTypes.Struct = {
        ...this.client!.session.get(topic),
        namespaces: params.namespaces
      };

      this.onSessionConnected(updatedSession);

      await onSessionUpdate(params.namespaces.algorand.accounts);
    });

    this.client.on("session_delete", async () => {
      this.reset();

      await onDisconnectSuccessful();
    });

    this.client.on("session_expire", async () => {
      this.reset();

      await onDisconnectSuccessful();
    });
  }

  private reset() {
    this.session = undefined;
  }

  async connect() {
    if (!this.client) {
      throw new Error("WalletConnect is not initialized");
    }

    try {
      const {uri, approval} = await this.client.connect({
        requiredNamespaces: DEFAULT_REQUIRED_NAMESPACES
      });

      if (uri) {
        this.web3Modal.openModal({
          uri,
          standaloneChains: [this.chainId]
        });
      }
      this.pairings = this.client.pairing.getAll({active: true});
      const session = await approval();

      this.onSessionConnected(session);
    } catch (error) {
      console.error("Failed to connect:", error);
      throw error;
    } finally {
      this.web3Modal.closeModal();
    }
  }

  getAccounts(): string[] {
    if (!this.session || !this.session.namespaces) {
      return [];
    }
    // to filter duplicate accounts from chains you're not requesting
    const filteredSession = {...this.session};

    if (filteredSession.namespaces.algorand) {
      filteredSession.namespaces.algorand.accounts =
        filteredSession.namespaces.algorand.accounts
          .filter((account) => DEFAULT_CHAINS.some((chain) => account.startsWith(chain)))
          .map((account) => {
            const parts = account.split(":");

            return parts[parts.length - 1];
          });
    }

    // Extract accounts from all namespaces and remove duplicates
    const allAccounts = [
      ...new Set(
        Object.values(filteredSession.namespaces)
          .map((namespace) => namespace.accounts)
          .flat()
      )
    ];

    return allAccounts;
  }

  async disconnect() {
    if (this.client === undefined || this.session === undefined) {
      throw new Error("WalletConnect is not initialized or not connected");
    }

    try {
      await this.client.disconnect({
        topic: this.session.topic,
        reason: getSdkError("USER_DISCONNECTED")
      });
      this.reset();
    } catch (error) {
      console.error("SignClient.disconnect failed:", error);
      throw error;
    }
  }

  async signTransaction(txGroups: SignerTransaction[][], signerAddress?: string) {
    try {
      if (!this.client || !this.session)
        throw new Error("WalletConnect is not initialized or not connected");

      const signTxnParams = getSignTxnRequestParams(txGroups, signerAddress);
      const request = formatJsonRpcRequest("algo_signTxn", [signTxnParams]);
      const response: Array<string | number[]> = await this.client
        .request({
          chainId: this.chainId,
          topic: this.session.topic,
          request
        })
        .then((res) => (res as Array<string | number[]>).filter(Boolean));

      return typeof response[0] === "string"
        ? (response as string[]).map(base64ToUint8Array)
        : (response as number[][]).map((item) => Uint8Array.from(item));
    } catch (error: any) {
      console.error("Transaction failed");

      throw error;
    }
  }
}

const walletConnectManager = new WalletConnectManager(PREFERRED_NETWORK);

export default walletConnectManager;
